import { FontAwesome } from "@expo/vector-icons"
import { createURL } from "expo-linking";
import React, { useEffect, useState, useRef } from "react";
import { Animated, Text, View } from "react-native"
import { Pressable } from 'react-native';
import { useDispatch, useSelector } from "react-redux"
import { CLOSE_TAB, SWITCH_TAB } from "@reduxLocal/actions/types"
import styles from "@stylesheet"
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from "expo-linear-gradient";
import { DefaultRootState } from "@reduxLocal/persistState";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useCallback } from "react";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";

const navigateToScreen = (navigation, tabData) => {
  const { page, route, params } = tabData;

  if (!page) {
    console.error("Invalid tab data - no page specified");
    return;
  }

  try {
    // Use any navigation type to bypass TypeScript strict checking
    const nav = navigation as any;
    
    // If we have route data from navigation state, use it for nested navigation
    if (route && route.routes && route.routes.length > 0) {
      const currentRoute = route.routes[route.index || route.routes.length - 1];
      if (currentRoute && currentRoute.state) {
        // Handle nested navigation state
        const nestedRoute = currentRoute.state.routes[currentRoute.state.index || currentRoute.state.routes.length - 1];
        nav.navigate(page, {
          screen: nestedRoute.name,
          params: { ...nestedRoute.params, ...params }
        });
      } else if (currentRoute) {
        // Handle simple nested navigation
        nav.navigate(page, {
          screen: currentRoute.name,
          params: { ...currentRoute.params, ...params }
        });
      } else {
        // Fallback to direct navigation
        nav.navigate(page, params);
      }
    } else {
      // Direct navigation to main screen
      nav.navigate(page, params);
    }
    
    console.log(`Successfully navigated to ${page}`, { route, params });
  } catch (error) {
    console.error("Navigation error:", error);
    // Fallback navigation
    try {
      (navigation as any).navigate(page);
    } catch (fallbackError) {
      console.error("Fallback navigation also failed:", fallbackError);
    }
  }
};

function splitByCapitals(input) {
  if (typeof input !== 'string') {
    console.warn('splitByCapitals received non-string:', input);
    return [];
  }
  return input.split(/(?=[A-Z])/);
}


export default function Tabs({ tabs, activeTab, currentTab }) {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const currentScreen = useSelector((state: DefaultRootState) => state.navigation.page);
  const route = useRoute();
  const theme = themeSelector();

  const getParams = useCallback((tabObject) => {
    // Try to get route data from the stored tab route first
    if (tabObject?.route && tabObject.route.routes) {
      return tabObject.route.routes[0];
    }
    
    // Fallback to current screen route data
    const { route } = tabObject || {};
    if (!route) {
      return null;
    }
    return route.routes && route.routes[0];
  }, [currentScreen]);

  const switchTab = () => {
    console.log('Switching to tab:', tabs);
    console.log('Tab route data:', tabs?.route);
    console.log('Tab params:', tabs?.params);
    
    dispatch({ type: SWITCH_TAB, payload: tabs.id });
    navigateToScreen(navigation, tabs);
  };

  const closeTab = async () => {
    dispatch({ type: CLOSE_TAB, payload: tabs.id });
    // Navigate to the next available tab or fallback to Welcome
    try {
      const remainingTabs = activeTab.filter(tab => tab.id !== tabs.id);
      if (remainingTabs.length > 0) {
        navigateToScreen(navigation, remainingTabs[0]);
      } else {
        // Use any navigation type to bypass TypeScript strict checking
        (navigation as any).navigate('Welcome');
      }
    } catch (error) {
      console.error("Error closing tab:", error);
      // Use any navigation type to bypass TypeScript strict checking
      (navigation as any).navigate('Welcome');
    }
  };

  const calculateFontSize = (tabName: string): number => {
    const baseFontSize = 20;
    const maxLength = 12;

    if (tabName.length <= maxLength) {
      return baseFontSize;
    }

    return baseFontSize * (maxLength / tabName.length);
  };


  return (
    <Pressable
      style={{ flex: 1, alignSelf: "stretch", minHeight: 40 }}
      onPress={switchTab}
      hitSlop={20}
      >
      {({ hovered }) => (
        <Animatable.View transition={['backgroundColor']} style={[
          activeTab[0]?.id === tabs?.id
            ? (hovered ? { backgroundColor: theme ? "rgba(200, 162, 200, 0.4)" : "rgba(135, 206, 235, 0.4)" } : { backgroundColor: theme ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)" })
            : (hovered ? { backgroundColor: theme ? "rgba(200, 162, 200, 0.2)" : "rgba(135, 206, 235, 0.2)" } : { backgroundColor: "transparent" }),
          {
            alignSelf: "stretch",
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 10,
            paddingTop: 0, // Removed extra padding
            borderWidth: 0.5,
            borderRadius: 5,
            borderColor: theme ? 'rgba(200, 162, 200, 0.3)' : 'rgba(135, 206, 235, 0.3)',
          }
        ]}>
          <View style={{ flex: 1, overflow: "hidden", justifyContent: 'center' }}>
              {
                getParams(tabs)?.state?.routes && getParams(tabs)?.state?.routes[currentTab]?.params &&
              <Animatable.Text animation={'slideInDown'} style={{ userSelect: "none", textAlign: "center", fontSize: 12 }}>
                  {getParams(tabs)?.state?.routes[currentTab]?.params?.title ? 
                    splitByCapitals(getParams(tabs)?.state?.routes[currentTab]?.params.title) :
                    ""}
                </Animatable.Text>
              }
            <Text numberOfLines={1} style={{ userSelect: "none", fontWeight: "bold", textAlign: "center", fontSize: 14, color: theme ? '#333' : '#fff' }}>
                {splitByCapitals(tabs.page || "Unknown")}
              </Text>
            </View>
          {currentTab > 1 && activeTab.length > 0 &&
            <Pressable style={{ marginLeft: 8, padding: 4 }} onPress={closeTab}>
              <FontAwesome name={"close"} size={14} color={theme ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)'} />
            </Pressable>
            }
        </Animatable.View>
      )}
      </Pressable>
  );
}

